import {
    ControlledPatternField,
    ControlledSelectField,
    ControlledTextField,
    ControlNumberField,
} from 'components/Form';
import { ChapterEnum, ContragentTypeEnum, MtrTypeEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { Grid, GridItem } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import type { ForwardedRef } from 'react';
import type React from 'react';
import { useEffect, forwardRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useIsUniquePriceListMtrCode } from './useIsUniquePriceListMtrCode';
import { useIsUniquePriceListMtrRowKey } from './useIsUniquePriceListMtrRowKey';

const supplyByItems = [
    {
        id: ContragentTypeEnum.Customer,
        label: 'Заказчик',
    },
    {
        id: ContragentTypeEnum.Contractor,
        label: 'Подрядчик',
    },
];

interface ICreateMtrFormProps {
    onSubmit: (values: any) => void;
    defaultValues?: any;
    setIsDirty?: React.Dispatch<React.SetStateAction<boolean>>;
    setHasErrors?: React.Dispatch<React.SetStateAction<boolean>>;
    chapter?: ChapterEnum | null;
}
const keyKey = 'key';
const codeValueKey = 'code';
const mtrTypeValueKey = 'mtrType';
const supplyByValueKey = 'supplyBy';

export const CreateMtrForm = forwardRef(
    (
        { defaultValues = {}, onSubmit, setIsDirty, setHasErrors, chapter }: ICreateMtrFormProps,
        formRef: ForwardedRef<HTMLFormElement>,
    ) => {
        const { control, handleSubmit, formState, watch, clearErrors, setError } = useForm({
            criteriaMode: 'all',
            defaultValues,
        });
        const { id: projectId } = useParams();
        const { projectData } = useProjectData(projectId);
        const contractId = useMemo(() => projectData?.contractId, [projectData?.contractId]);

        const keyValue = watch?.(keyKey);
        const codeValue = watch?.(codeValueKey);
        const mtrTypeValue = watch?.(mtrTypeValueKey);
        const supplyByValue = watch?.(supplyByValueKey);
        const isDirtyKeyValue = formState?.dirtyFields?.[keyKey];
        const isDirtyCodeValue = formState?.dirtyFields?.[codeValueKey];

        const { result: isKeyValueUnique } = useIsUniquePriceListMtrRowKey({
            contractId,
            mtrRowId: null,
            isDirtyCodeValue: isDirtyKeyValue,
            key: keyValue,
        });

        const { result: isCodeValueUnique } = useIsUniquePriceListMtrCode({
            contractId,
            mtrRowId: null,
            code: codeValue,
            isDirtyCodeValue,
            mtrType: mtrTypeValue?.id,
            supplyBy: supplyByValue?.id,
        });

        useEffect(() => {
            if (keyValue && !isKeyValueUnique) {
                setError?.(keyKey, { message: 'МТР с таким кодом уже существует' });
            } else {
                clearErrors?.(keyKey);
            }
        }, [clearErrors, isKeyValueUnique, keyValue, setError]);

        useEffect(() => {
            if (codeValue && !isCodeValueUnique) {
                setError?.(codeValueKey, {
                    message: 'Шифр МТР уже существует в рамках выбранного типа и оператора поставки',
                });
            } else {
                clearErrors?.(codeValueKey);
            }
        }, [clearErrors, codeValue, isCodeValueUnique, isKeyValueUnique, keyValue, setError]);

        const mtrTypeItems = useMemo(
            () => [
                ...(![ChapterEnum.Chapter8, ChapterEnum.Chapter9].includes(chapter as ChapterEnum)
                    ? [
                          {
                              id: MtrTypeEnum.Equipment,
                              label: 'Оборудование',
                          },
                      ]
                    : []),
                {
                    id: MtrTypeEnum.Material,
                    label: 'Материал',
                },
            ],
            [chapter],
        );

        useEffect(() => {
            if (setIsDirty) {
                setIsDirty(formState.isDirty);
            }
        }, [formState.isDirty, setIsDirty]);

        useEffect(() => {
            if (setHasErrors) {
                if (!isKeyValueUnique || !isCodeValueUnique) {
                    setHasErrors(true);
                } else {
                    setHasErrors(false);
                }
            }
        }, [isCodeValueUnique, isKeyValueUnique, setHasErrors]);

        return (
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                <Grid rowGap="s" cols="1">
                    <GridItem>
                        <ControlledPatternField
                            name={codeValueKey}
                            control={control}
                            label="Шифр"
                            format="####"
                            rules={{
                                ...requiredBaseRule,
                                validate: (val) => {
                                    if (val === '0000') {
                                        return 'Шифр МТР не может быть нулевым';
                                    }

                                    if (val && val.split(' ').length !== 1) {
                                        return "Шифр МТР не соответствует маске 'хххх', где х - цифра от 0 до 9";
                                    }

                                    return true;
                                },
                            }}
                        />
                    </GridItem>

                    <GridItem>
                        <ControlledTextField
                            name="name"
                            label="Наименование"
                            control={control}
                            type="textarea"
                            rules={{
                                ...requiredBaseRule,
                                maxLength: {
                                    value: 1500,
                                    message: 'Превышено 1500 символов',
                                },
                            }}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="unit"
                            label="Ед. изм."
                            control={control}
                            type="text"
                            rules={requiredBaseRule}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="unitWeight"
                            label="Вес, т."
                            control={control}
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="unitCost"
                            label="Стоимость единицы руб. без НДС"
                            control={control}
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            rules={requiredBaseRule}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name={supplyByValueKey}
                            label="Поставка"
                            control={control}
                            items={supplyByItems}
                            rules={requiredBaseRule}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name={mtrTypeValueKey}
                            label="Тип"
                            control={control}
                            items={mtrTypeItems}
                            rules={requiredBaseRule}
                        />
                    </GridItem>

                    <GridItem>
                        <ControlledTextField name="key" label="Код" control={control} type="text" />
                    </GridItem>

                    <GridItem>
                        <ControlledTextField
                            name="justification"
                            label="Обоснование"
                            control={control}
                            type="textarea"
                            cols={30}
                            rules={requiredBaseRule}
                        />
                    </GridItem>
                </Grid>
            </form>
        );
    },
);

CreateMtrForm.displayName = 'CreateMtrForm';
